import React, { useEffect, useState } from "react";
import { Card, Table, Tag, Typography, Empty, Spin, Divider, Space } from "antd";
import { SafetyCertificateOutlined, CarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../../../variables/api/axiosInstance";

const { Text, Title } = Typography;

const TYPE_COLORS = { CIMA: "blue", CEDEAO: "green", DIGITALE: "geekblue" };

/**
 * Historique UNIFIÉ des attestations (CIMA / CEDEAO / DIGITALE) portées par chaque engin du
 * contrat, lu depuis la trace append-only (/api/attestation-imprimee/engin/{codeId}). Survit à la
 * réinitialisation du numéro d'attestation en affaire nouvelle.
 */
const HistoriqueAttestationsContent = ({ contrat }) => {
    const engins = contrat?.engins || [];
    const [loading, setLoading] = useState(false);
    const [historiques, setHistoriques] = useState({}); // { [enginCodeId]: [rows] }

    useEffect(() => {
        const codeIds = engins.map((e) => e?.codeId).filter(Boolean);
        if (codeIds.length === 0) return;
        setLoading(true);
        Promise.all(
            codeIds.map((codeId) =>
                axiosInstance
                    .get(`/api/attestation-imprimee/engin/${codeId}`)
                    .then((res) => [codeId, Array.isArray(res.data) ? res.data : []])
                    .catch(() => [codeId, []])
            )
        )
            .then((entries) => setHistoriques(Object.fromEntries(entries)))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contrat?.codeId]);

    const columns = [
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            width: 110,
            render: (t) => <Tag color={TYPE_COLORS[t] || "default"}>{t || "—"}</Tag>
        },
        {
            title: "N° attestation",
            dataIndex: "numeroAttestation",
            key: "numeroAttestation",
            render: (v) => (v ? <Text strong>{v}</Text> : <Text type="secondary">—</Text>)
        },
        {
            title: "N° police",
            dataIndex: "numeroPolice",
            key: "numeroPolice",
            render: (v) => v || <Text type="secondary">—</Text>
        },
        {
            title: "Date d'effet",
            dataIndex: "dateEffet",
            key: "dateEffet",
            render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : <Text type="secondary">—</Text>)
        },
        {
            title: "Imprimée le",
            dataIndex: "dateImpression",
            key: "dateImpression",
            render: (v, r) => {
                const d = v || r.createdAt;
                return d ? dayjs(d).format("DD/MM/YYYY HH:mm") : <Text type="secondary">—</Text>;
            }
        },
        {
            title: "Compagnie",
            dataIndex: "organismeNom",
            key: "organismeNom",
            render: (v, r) => v || r.organismeCode || <Text type="secondary">—</Text>
        },
        {
            title: "Par",
            dataIndex: "createdBy",
            key: "createdBy",
            render: (v) => v || <Text type="secondary">—</Text>
        }
    ];

    if (engins.length === 0) {
        return (
            <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 32 }}>
                <Empty description="Aucun engin sur ce contrat" />
            </Card>
        );
    }

    return (
        <Spin spinning={loading}>
            {engins.map((eng, idx) => {
                const rows = historiques[eng?.codeId] || [];
                return (
                    <Card
                        key={eng?.codeId || idx}
                        style={{ borderRadius: 12, marginBottom: 20 }}
                        bodyStyle={{ padding: 24 }}
                    >
                        <Space align="center" style={{ marginBottom: 8 }}>
                            <CarOutlined style={{ color: "#2563eb" }} />
                            <Title level={5} style={{ margin: 0 }}>
                                {eng?.immatriculation || "Engin sans immatriculation"}
                            </Title>
                            {eng?.numeroAttestation ? (
                                <Tag icon={<SafetyCertificateOutlined />} color="blue">
                                    N° courant : {eng.numeroAttestation}
                                </Tag>
                            ) : (
                                <Tag color="default">Aucun n° courant</Tag>
                            )}
                        </Space>
                        <Divider style={{ margin: "8px 0 16px" }} />
                        {rows.length === 0 ? (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Aucune attestation historisée pour cet engin"
                            />
                        ) : (
                            <Table
                                size="small"
                                rowKey={(r) => r.id}
                                columns={columns}
                                dataSource={rows}
                                pagination={rows.length > 10 ? { pageSize: 10 } : false}
                            />
                        )}
                    </Card>
                );
            })}
        </Spin>
    );
};

export default HistoriqueAttestationsContent;
